import React, { Fragment, PureComponent } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  Grid,
  MenuItem,
  Select,
  Toolbar,
  withStyles,
  withWidth
} from '@material-ui/core';
import { FiStar } from 'react-icons/fi';
import { IoChevronDownOutline } from 'react-icons/io5';
import faker from 'faker';
import PropTypes from 'prop-types';
import { compose } from 'recompose';

import TrendingChart from './TrendingChart';
import PivotChart from './PivotChart';
import { formatDuration, formatCurrency } from '../../helpers/format';

const styles = (theme) => ({
  root: {
    padding: theme.spacing(4.5, 7, 4.5, 5),
    [theme.breakpoints.only('xs')]: {
      padding: theme.spacing(1.5, 1)
    },
    [theme.breakpoints.only('sm')]: {
      padding: theme.spacing(3, 2)
    }
  },
  label: {
    color: '#666E79',
    fontSize: theme.spacing(1.125) + 'px',
    lineHeight: theme.spacing(1.5) + 'px'
  },
  groupButton: {
    width: theme.spacing(5)
  },
  tool1: {
    [theme.breakpoints.up('md')]: {
      justifyContent: 'flex-end'
    }
  },
  speedIcon: {
    width: theme.spacing(3),
    height: theme.spacing(3)
  },
  badge: {
    position: 'absolute',
    top: 0,
    right: theme.spacing(3),
    [theme.breakpoints.only('xs')]: {
      right: theme.spacing(1)
    },
    [theme.breakpoints.only('sm')]: {
      right: theme.spacing(2)
    },
    backgroundColor: '#1C9DE8',
    color: '#fff',
    padding: theme.spacing(0.25, 1, 0.5, 1),
    borderRadius: theme.spacing(0, 0, 1, 1),
    fontSize: theme.spacing(1.125),
    lineHeight: theme.spacing(1.5) + 'px'
  }
})

const StyledButton = withStyles(theme => ({
  root: {
    width: theme.spacing(8)
  },
  contained: {
    backgroundColor: '#9AC802',
    color: '#111112',
    '&:hover': {
      backgroundColor: '#9AC802'
    },
    '&:focus': {
      backgroundColor: '#9AC802'
    }
  },
  outlined: {
    borderColor: '#9AC802',
    color: '#9AC802'
  },
  label: {
    fontSize: theme.spacing(1.125) + 'px',
    lineHeight: theme.spacing(1.25) + 'px',
    fontWeight: '500',
    whiteSpace: 'nowrap'
  }
}))(Button);

const StyledSelect = withStyles(theme => ({
  root: {
    width: theme.spacing(16.5),
    borderColor: 'rgba(102, 110, 121, 0.5)',
    borderStyle: 'solid',
    borderWidth: 1,
    borderRaduis: theme.spacing(0.5),
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(3),
    fontWeight: '500',
    fontSize: theme.spacing(1.5) + 'px',
    lineHeight: theme.spacing(2) + 'px'
  }
}))(Select);

const StyledDropDownIcon = (props) => (
  <div style={{
    position: 'absolute',
    right: 4
  }}>
    <IoChevronDownOutline color="#666E79" fontSize={16} />
  </div>
)

class Content extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      unit: '$',
      sortBy: 'trending',
      records: this.generateData()
    };
  }

  generateData() {
    const data = [];
    for (let i = 0; i < 5; i++) {
      data.push({
        speed: faker.datatype.number({ min: 1, max: 10, precision: 0.1 }),
        remainingSeconds: faker.datatype.number({ min: 1000, max: 10000 }),
        amount: faker.datatype.number({ min: 0, max: 1000000 }),
        badge: faker.datatype.boolean() ? faker.datatype.number({ min: 1, max: 10 }) : 0
      });
    }
    return data;
  }

  render() {
    return (
      <main style={{ flexGrow: 1 }}>
        <Toolbar />
        <Box className={this.props.classes.root}>
          <Grid container alignItems="center">
            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" mb={{ xs: 1.5, sm: 2.5, md: 3.5 }}>
                <Box mr={1} className={this.props.classes.label}>Select assets, types and period:</Box>
                <StyledButton variant="outlined">FILTER</StyledButton>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" mb={{ xs: 1.5, sm: 2.5, md: 3.5 }} className={this.props.classes.tool1}>
                <Box mr={1} className={this.props.classes.label}>Units:</Box>
                <ButtonGroup>
                  <StyledButton
                    className={this.props.classes.groupButton}
                    onClick={() => this.setState({ unit: '%' })}
                    variant={this.state.unit === '%' && 'contained'}
                  >
                    %
                  </StyledButton>
                  <StyledButton
                    className={this.props.classes.groupButton}
                    onClick={() => this.setState({ unit: '$' })}
                    variant={this.state.unit === '$' && 'contained'}
                  >
                    $
                  </StyledButton>
                </ButtonGroup>
                <Box ml={{ xs: 2, sm: 3, md: 4 }} mr={1} whiteSpace="nowrap" className={this.props.classes.label}>Sort by:</Box>
                <StyledSelect
                  value={this.state.sortBy}
                  onChange={e => this.setState({ sortBy: e.target.value })}
                  disableUnderline
                  IconComponent={StyledDropDownIcon}
                >
                  <MenuItem value="trending">Trending</MenuItem>
                  <MenuItem value="amount">Amount</MenuItem>
                  <MenuItem value="time">Time</MenuItem>
                </StyledSelect>
              </Box>
            </Grid>
          </Grid>
          {this.state.records.map(this.renderRecord)}
        </Box>
      </main>
    );
  }

  renderRecord = (record, index) => (
    <Box
      key={index}
      border="1px solid rgba(102, 110, 121, 0.5)"
      mb={{ xs: 1.5, sm: 2, md: 2.5 }}
      bgcolor="#272A2D"
      borderRadius={this.props.theme.spacing(1)}
      position="relative"
    >
      <Box px={{ xs: 0.5, sm: 1, md: 1.5 }}>
        {this.props.width === 'xs' || this.props.width === 'sm' ? (
          <Fragment>
            <Grid container alignItems="center">
              <Grid item xs={6}>
                {this.renderItem1(record)}
              </Grid>
              <Grid item xs={6}>
                {this.renderItem5(record)}
              </Grid>
            </Grid>
            <Box mt={1}>
              <Divider />
            </Box>
            <Grid container alignItems="center">
              <Grid item xs={4}>
                {this.renderItem2(record)}
              </Grid>
              <Grid item xs={4}>
                {this.renderItem3(record)}
              </Grid>
              <Grid item xs={4} >
                {this.renderItem4(record)}
              </Grid>
            </Grid>
          </Fragment>
        ) : (
          <Grid container alignItems="center">
            <Grid item md={3}>
              {this.renderItem1(record)}
            </Grid>
            <Grid item md={2}>
              {this.renderItem2(record)}
            </Grid>
            <Grid item md={2}>
              {this.renderItem3(record)}
            </Grid>
            <Grid item md={2}>
              {this.renderItem4(record)}
            </Grid>
            <Grid item md={3}>
              {this.renderItem5(record)}
            </Grid>
          </Grid>
        )}
      </Box>
      {record.badge > 0 && (
        <Box className={this.props.classes.badge}>
          {record.badge}
        </Box>
      )}
    </Box>
  )

  renderItem1(record) {
    return (
      <Box display="flex" py={2}>
        <FiStar color="#9AC802" fontSize={this.props.theme.spacing(3)} />
        <Box flex={1} ml={1} mr={{ xs: 1, sm: 1.5, md: 2 }}>
          <Box
            fontWeight="500"
            fontSize={this.props.theme.spacing(2.5) + 'px'}
            lineHeight={this.props.theme.spacing(3) + 'px'}
            color="#fff"
          >
            ETH 48 hours
          </Box>
          <Box
            fontWeight="500"
            fontSize={this.props.theme.spacing(1.5) + 'px'}
            lineHeight={this.props.theme.spacing(2) + 'px'}
            color="#666E79"
            mt={0.5}
          >
            Crypto
          </Box>
        </Box>
        <Divider orientation="vertical" flexItem />
      </Box>
    );
  }

  renderItem2(record) {
    return (
      <Box px={{ xs: 1, md: 1.5 }} py={2}>
        <Box display="flex" mb={1}>
          <img alt="" src={require('../../assets/images/speed.png')} className={this.props.classes.speedIcon} />
          <Box
            ml={{ xs: 1, md: 1.5 }}
            fontWeight="300"
            fontSize={this.props.theme.spacing(1.75)}
            lineHeight={this.props.theme.spacing(3) + 'px'}
            color="#fff"
          >
            2,2x
          </Box>
        </Box>
        <Box
          fontWeight="500"
          fontSize={this.props.theme.spacing(1.5)}
          lineHeight={this.props.theme.spacing(2) + 'px'}
          color="#666E79"
        >
          Outcome in {formatDuration(record.remainingSeconds)}
        </Box>
      </Box>
    );
  }

  renderItem3(record) {
    return (
      <TrendingChart
        duration={this.props.duration}
        height={this.props.theme.spacing(7.5)}
      />
    );
  }

  renderItem4(record) {
    return (
      <Box px={{ xs: 1, md: 1.5 }}>
        <PivotChart
          duration={this.props.duration}
          height={this.props.theme.spacing(7.5)}
        />
      </Box>
    );
  }

  renderItem5(record) {
    return (
      <Box px={{ xs: 1, md: 1.5 }} py={2}>
        <Box
          textAlign="right"
          fontFamily="Roboto Mono"
          fontWeight="500"
          fontSize={this.props.theme.spacing(2.5)}
          lineHeight={this.props.theme.spacing(3) + 'px'}
          color="#fff"
        >
          {formatCurrency(record.amount)}
        </Box>
        <Box
          mt={0.5}
          textAlign="right"
          fontWeight="500"
          fontSize={this.props.theme.spacing(1.5)}
          lineHeight={this.props.theme.spacing(2) + 'px'}
          color="#666E79"
        >
          Total commitment funds
        </Box>
      </Box>
    );
  }
}

Content.propTypes = {
  duration: PropTypes.string.isRequired
};

export default compose(
  withStyles(styles, { withTheme: true }),
  withWidth()
)(Content);
